/*
 * timeline-types — pure types + math for the History tab's explorable timeline.
 *
 * SIBLING to chart-types.ts, deliberately not a generalization of it: the live
 * chart's math is hardwired (correctly) to "trailing window ending at now" over
 * a RingBuffer, with carry-forward and no gap-cutting. The timeline is the
 * opposite regime — an arbitrary {startMs,endMs} viewport over a plain sorted
 * bucket array, with HONEST GAPS (an absent bucket is "nothing was recorded",
 * never a value to draw through). Sharing one parameterized implementation
 * would couple two behaviours that must evolve apart; only the palette and
 * value formatters are genuinely common (imported from chart-types).
 */
import type { HistoryBucket } from '../../../core/bridge/connect-client';

export interface TimelineViewport {
  startMs: number;
  endMs: number;
}

// ── zoom-level ladder ────────────────────────────────────────────────────────

/**
 * The bucket sizes the client ever asks the historian for (seconds). A fixed
 * ladder — rather than a continuous pixel-perfect size — so pans at the same
 * zoom hit the same bucket grid (server buckets are epoch-aligned to the
 * bucket size, so equal sizes ⇒ identical bucket boundaries ⇒ cache hits).
 */
export const BUCKET_LADDER_SECONDS: readonly number[] = [1, 2, 5, 15, 30, 60, 300, 900, 3600, 21_600, 86_400];

/** Aim for roughly one bucket per this many CSS px — enough for min/max
 *  envelopes to render as a band rather than noise, without under-resolving. */
const TARGET_PX_PER_BUCKET = 2;

/**
 * Pick the resolution for a viewport: `null` = raw samples (QueryRange), a
 * number = bucket seconds (QueryAggregated). Raw when even 1s buckets would
 * over-resolve the screen — i.e. the viewport is narrow enough that the raw
 * row count is bounded and every sample can matter.
 */
export function pickBucketSeconds(viewport: TimelineViewport, pixelWidth: number): number | null {
  const spanSec = Math.max(1e-3, (viewport.endMs - viewport.startMs) / 1000);
  const px = Math.max(64, pixelWidth);
  const ideal = spanSec / (px / TARGET_PX_PER_BUCKET);
  if (ideal < 1) return null;
  for (const rung of BUCKET_LADDER_SECONDS) if (rung >= ideal) return rung;
  return BUCKET_LADDER_SECONDS[BUCKET_LADDER_SECONDS.length - 1];
}

/** Snap a fetch range OUT to bucket boundaries, so overlapping fetches at one
 *  level land on identical server buckets (the poor man's tile alignment). */
export function snapRangeToBuckets(startMs: number, endMs: number, bucketSeconds: number): { startMs: number; endMs: number } {
  const b = bucketSeconds * 1000;
  return { startMs: Math.floor(startMs / b) * b, endMs: Math.ceil(endMs / b) * b };
}

/**
 * The rung a zoom-IN crosses to next from `activeSec` (`null` = raw samples).
 * `undefined` = nothing finer exists (already raw). Finer than 2s is RAW, not
 * the 1s rung: pickBucketSeconds only ever selects 1s when the ideal lands on
 * exactly 1 — a shrinking span skips straight from 2s to raw.
 */
export function nextFinerRung(activeSec: number | null): number | null | undefined {
  if (activeSec === null) return undefined;
  const idx = BUCKET_LADDER_SECONDS.indexOf(activeSec);
  if (idx === -1) return undefined;
  if (idx <= 1) return null;
  return BUCKET_LADDER_SECONDS[idx - 1];
}

/**
 * The rung a zoom-OUT crosses to next. Coarser than raw is the 2s rung (a
 * growing span's ideal passes 1 without landing on it exactly — see above).
 * `undefined` = already at the top of the ladder.
 */
export function nextCoarserRung(activeSec: number | null): number | undefined {
  if (activeSec === null) return BUCKET_LADDER_SECONDS[1];
  const idx = BUCKET_LADDER_SECONDS.indexOf(activeSec);
  if (idx === -1 || idx === BUCKET_LADDER_SECONDS.length - 1) return undefined;
  return BUCKET_LADDER_SECONDS[idx + 1];
}

// ── viewport gesture math ────────────────────────────────────────────────────

/** Smallest/widest sensible viewport spans (zoom clamps). */
export const MIN_VIEWPORT_SPAN_MS = 2_000;
export const MAX_VIEWPORT_SPAN_MS = 366 * 86_400_000;

/** Zoom by `factor` (>1 = in) keeping the time under `anchorFrac` (0..1 across
 *  the plot) pinned — the flow-canvas zoomAt math on the time axis. */
export function zoomViewport(v: TimelineViewport, factor: number, anchorFrac: number, nowMs: number): TimelineViewport {
  const span = v.endMs - v.startMs;
  const newSpan = Math.min(MAX_VIEWPORT_SPAN_MS, Math.max(MIN_VIEWPORT_SPAN_MS, span / factor));
  if (newSpan === span) return v;
  const anchorT = v.startMs + span * anchorFrac;
  let startMs = anchorT - newSpan * anchorFrac;
  let endMs = startMs + newSpan;
  if (endMs > nowMs) {
    // The future holds no data — slide back instead of showing guaranteed blank.
    endMs = nowMs;
    startMs = endMs - newSpan;
  }
  return { startMs, endMs };
}

/**
 * Keep a view inside fixed bounds — a replay's window, whose data stops at
 * both ends. The span is kept and the view slides back in; a view wider than
 * the bounds becomes the bounds. `zoomViewport`/`panViewport` clamp only the
 * end (to "now"), which is right for live history and half of what a replay
 * needs.
 */
export function clampViewportToBounds(v: TimelineViewport, bounds: TimelineViewport): TimelineViewport {
  const boundSpan = bounds.endMs - bounds.startMs;
  const span = Math.min(v.endMs - v.startMs, boundSpan);
  let startMs = v.startMs;
  if (startMs + span > bounds.endMs) startMs = bounds.endMs - span;
  if (startMs < bounds.startMs) startMs = bounds.startMs;
  return { startMs, endMs: startMs + span };
}

/** Pan by a fraction of the current span (positive = toward the past). */
export function panViewport(v: TimelineViewport, spanFrac: number, nowMs: number): TimelineViewport {
  const span = v.endMs - v.startMs;
  let startMs = v.startMs - span * spanFrac;
  let endMs = startMs + span;
  if (endMs > nowMs) {
    endMs = nowMs;
    startMs = endMs - span;
  }
  return { startMs, endMs };
}

// ── sorted-array lookup ─────────────────────────────────────────────────────

/**
 * Index of the first bucket with `tsMs >= t` in a tsMs-sorted array, or
 * `buckets.length` when every bucket is earlier. The resident array is 3–5×
 * the visible set (prefetch margins + retention hysteresis, several levels),
 * and the y-scale, the segmenter and the status chip each used to walk all of
 * it per frame to find the visible slice — this jumps there instead.
 */
export function lowerBound(buckets: readonly HistoryBucket[], tsMs: number): number {
  let lo = 0;
  let hi = buckets.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (buckets[mid].tsMs < tsMs) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// ── gap-aware segmentation ──────────────────────────────────────────────────

/**
 * Split sorted buckets into contiguous segments for drawing, breaking where a
 * bucket is MISSING. The historian never returns empty buckets — a bucket with
 * zero samples is simply absent from the reply — so a gap is detected as a
 * jump between consecutive bucket timestamps larger than one bucket (1.5×
 * for float slack). Raw samples (`bucketMs === null`) are publish-on-change:
 * a long quiet interval means "held constant", NOT missing — one segment.
 *
 * `from`/`to` (inclusive, clamped) restrict the walk to an index window; the
 * returned indices are absolute. The chart passes the slice that can touch
 * the viewport so off-screen resident buckets cost nothing per frame.
 */
export function segmentBuckets(
  buckets: readonly HistoryBucket[],
  bucketMs: number | null,
  from = 0,
  to = buckets.length - 1,
): Array<[number, number]> {
  from = Math.max(0, from);
  to = Math.min(buckets.length - 1, to);
  if (to < from) return [];
  if (bucketMs === null) return [[from, to]];
  const segs: Array<[number, number]> = [];
  let s = from;
  for (let i = from + 1; i <= to; i++) {
    if (buckets[i].tsMs - buckets[i - 1].tsMs > bucketMs * 1.5) {
      segs.push([s, i - 1]);
      s = i;
    }
  }
  segs.push([s, to]);
  return segs;
}

/**
 * Clamp not-yet-fetched (veil) ranges so nothing "loading" is claimed before
 * the recorded-data boundary: the region before `dataStartMs` is KNOWN empty
 * (the historian's bounds said so), so hatching it as "still loading" would
 * promise data that can never arrive. Ranges entirely pre-data drop out;
 * ranges straddling the boundary keep only their post-boundary part.
 * `dataStartMs === null` (bounds unknown) passes everything through.
 */
export function clampBlindRanges(blind: ReadonlyArray<[number, number]>, dataStartMs: number | null): Array<[number, number]> {
  if (dataStartMs === null) return [...blind];
  const out: Array<[number, number]> = [];
  for (const [t0, t1] of blind) {
    const s = Math.max(t0, dataStartMs);
    if (t1 > s) out.push([s, t1]);
  }
  return out;
}

/**
 * True when a quick-range preset of `spanMs` reaches further back than the
 * device has recorded data (its lifespan = `nowMs - dataStartMs`). Unknown
 * bounds (`null`) never disable anything — bounds are decoration, and a
 * failed fetch must not take the presets away.
 */
export function presetExceedsLifespan(spanMs: number, dataStartMs: number | null, nowMs: number): boolean {
  if (dataStartMs === null) return false;
  return spanMs > nowMs - dataStartMs;
}

/**
 * Keep a window from starting before the recorded-data boundary. A window
 * that merely reaches past it is SHIFTED right (its span survives, so a pan
 * that hits the wall stops rather than shrinks); one wider than the whole
 * lifespan is pinned to [dataStart, maxEnd]. `null` bounds pass through.
 */
export function clampToDataStart(
  startMs: number,
  endMs: number,
  dataStartMs: number | null,
  maxEndMs: number,
): { startMs: number; endMs: number } {
  if (dataStartMs === null || startMs >= dataStartMs) return { startMs, endMs };
  const shift = dataStartMs - startMs;
  return { startMs: dataStartMs, endMs: Math.min(endMs + shift, maxEndMs) };
}

// ── y-axis scale (visible buckets ONLY — never the prefetch margin) ─────────

export interface TimelineYScale {
  minValue: number;
  maxValue: number;
  ticks: { value: number; y: number }[];
}

/**
 * Min/max over the buckets INSIDE the viewport, 5% pad, constant → ±1 band.
 * Scoping to the visible window (not everything loaded) is what keeps an
 * off-screen prefetch landing from silently rescaling the axis — "nothing
 * moves that the user did not move". Returns null when nothing is visible
 * (caller holds the previous scale rather than snapping to a default).
 *
 * `tickIntervals` divides the value range into that many steps (ticks =
 * intervals + 1) — short stacked lanes pass 2 so labels don't crowd.
 */
export function calculateTimelineYScale(
  buckets: readonly HistoryBucket[],
  viewport: TimelineViewport,
  plotHeight: number,
  tickIntervals = 4,
): TimelineYScale | null {
  let lo = Infinity;
  let hi = -Infinity;
  // Visible buckets only — deliberately excludes the off-screen sentinel
  // buckets the viewport controller retains past each edge, so a carried-in
  // line can enter from outside the y-range (clipped) without rescaling an
  // axis the user didn't move. Jump to the first visible bucket rather than
  // testing every resident one: this runs per lane per painted frame.
  for (let i = lowerBound(buckets, viewport.startMs); i < buckets.length && buckets[i].tsMs <= viewport.endMs; i++) {
    const b = buckets[i];
    if (b.min < lo) lo = b.min;
    if (b.max > hi) hi = b.max;
  }
  if (lo > hi) return null;
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  } else {
    const pad = (hi - lo) * 0.05;
    lo -= pad;
    hi += pad;
  }
  const ticks: TimelineYScale['ticks'] = [];
  for (let i = 0; i <= tickIntervals; i++) {
    ticks.push({ value: lo + ((hi - lo) * i) / tickIntervals, y: plotHeight - (i * plotHeight) / tickIntervals });
  }
  return { minValue: lo, maxValue: hi, ticks };
}

// ── x-axis ticks (absolute clock labels, not "-30s ago") ────────────────────

const X_TICK_STEPS_MS: readonly number[] = [
  1_000, 2_000, 5_000, 10_000, 15_000, 30_000, 60_000, 120_000, 300_000, 600_000, 900_000, 1_800_000, 3_600_000, 7_200_000, 10_800_000,
  21_600_000, 43_200_000, 86_400_000, 172_800_000, 604_800_000, 1_209_600_000, 2_592_000_000,
];
const TARGET_PX_PER_X_TICK = 110;

export interface TimelineXTick {
  x: number;
  label: string;
  /** Second line (date) shown when the step crosses days. */
  sub?: string;
}

const p2 = (n: number): string => String(n).padStart(2, '0');

export function formatTimelineTime(tsMs: number, stepMs: number): string {
  const d = new Date(tsMs);
  if (stepMs < 60_000) return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
  if (stepMs < 86_400_000) return `${p2(d.getHours())}:${p2(d.getMinutes())}`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** Full-precision stamp for the crosshair readout (date only when it matters). */
export function formatCrosshairTime(tsMs: number, viewportSpanMs: number): string {
  const d = new Date(tsMs);
  const hms = `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
  return viewportSpanMs > 86_400_000 ? `${d.getMonth() + 1}/${d.getDate()} ${hms}` : hms;
}

export function calculateTimelineXTicks(viewport: TimelineViewport, plotWidth: number): TimelineXTick[] {
  const span = viewport.endMs - viewport.startMs;
  if (span <= 0 || plotWidth <= 0) return [];
  const ideal = span / Math.max(1, plotWidth / TARGET_PX_PER_X_TICK);
  const step = X_TICK_STEPS_MS.find((s) => s >= ideal) ?? X_TICK_STEPS_MS[X_TICK_STEPS_MS.length - 1];
  const ticks: TimelineXTick[] = [];
  let lastDay = -1;
  for (let t = Math.ceil(viewport.startMs / step) * step; t <= viewport.endMs; t += step) {
    const x = ((t - viewport.startMs) / span) * plotWidth;
    const tick: TimelineXTick = { x, label: formatTimelineTime(t, step) };
    if (step < 86_400_000 && span > 6 * 3_600_000) {
      // Long intraday spans: stamp the date once per day boundary crossed.
      const d = new Date(t);
      if (d.getDate() !== lastDay) {
        tick.sub = `${d.getMonth() + 1}/${d.getDate()}`;
        lastDay = d.getDate();
      }
    }
    ticks.push(tick);
  }
  return ticks;
}

// ── time→x mapping (shared by drawing + hit-testing so they always agree) ───

export function timeToX(tsMs: number, viewport: TimelineViewport, plotWidth: number): number {
  return ((tsMs - viewport.startMs) / (viewport.endMs - viewport.startMs)) * plotWidth;
}
export function xToTime(x: number, viewport: TimelineViewport, plotWidth: number): number {
  return viewport.startMs + (x / plotWidth) * (viewport.endMs - viewport.startMs);
}
